import { vn } from 'vend-number'
import { useT } from '@transifex/react'
import { PERMISSIONS } from '../../../../constants'
import {
  getOutstandingBalance,
  isReturn,
  isValidCustomer,
} from '../../../../functions/sale'
import {
  useRetailerSettingsManager,
  useUserManager,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSaleAsSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import { SaleStatus } from '../../../../models/sale.constants'
import { containsGiftCard } from '../../../../functions/lineItem'
import { CustomerPaymentOptionsProps } from '../CustomerPaymentOptions'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { FEATURE_ON_ACCOUNT_PAYMENT_TYPE } from '../../../../constants/wr-features'

export type CustomerPaymentOptionsHookProps = Pick<
  CustomerPaymentOptionsProps,
  'systemPaymentTypes' | 'pay' | 'laybyOnAccountSale'
>
/**
 * Build the customer payment buttons:
 * layby, onaccount, loyalty and store credits
 */
export const useCustomerPaymentOptions = (
  props: CustomerPaymentOptionsHookProps
) => {
  const { systemPaymentTypes, pay, laybyOnAccountSale } = props
  const t = useT()
  const userManager = useUserManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const currentSale = useAppSelector(selectCurrentSaleAsSale)
  const balance = getOutstandingBalance(currentSale)
  const hasNegativeBalance = vn(balance).isNeg()
  const hasValidCustomer = isValidCustomer(currentSale.customer)
  const userCanIssueStoreCredit = userManager.hasPermission(
    PERMISSIONS.STORE_CREDIT_ISSUE_RETURN
  )
  const notReturnSaleAndPositiveBalance =
    !isReturn(currentSale) && !hasNegativeBalance

  const loyaltyPaymentType = systemPaymentTypes.loyaltyPaymentType
  const paymentOptions = []

  // loyalty
  let loyaltyButtonConfig
  if (loyaltyPaymentType && notReturnSaleAndPositiveBalance) {
    loyaltyButtonConfig = {
      id: 'loyalty',
      label: t('Loyalty', {
        _context: 'Loyalty payment type button label',
      }),
      action: () => pay(loyaltyPaymentType),
      isDisabled: false,
    }
    paymentOptions.push(loyaltyButtonConfig)
  }

  // layby payment
  const hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()
  if (
    notReturnSaleAndPositiveBalance ||
    (hasXeroEnabled && isReturn(currentSale) && !hasNegativeBalance)
  ) {
    if (
      currentSale.displayStatus !== SaleStatus.ACCOUNT_SALE &&
      userManager.hasPermission(PERMISSIONS.SALE_LAYBY) &&
      !containsGiftCard(currentSale.lineItems)
    ) {
      const laybyButtonConfig = {
        id: SaleStatus.LAYBY_SALE,
        label: t('Layaway', {
          _context: 'Layaway payment type button label',
        }),
        action: () => laybyOnAccountSale(SaleStatus.LAYBY_SALE),
        isDisabled: false,
      }
      paymentOptions.push(laybyButtonConfig)
    }
  }

  // store credit payment
  const isStoreCreditEnabled = retailerSettingsManager.isStoreCreditEnabled()

  const storeCreditPaymentType = systemPaymentTypes.storeCreditPaymentType
  if (
    isStoreCreditEnabled &&
    storeCreditPaymentType &&
    (!hasNegativeBalance || userCanIssueStoreCredit)
  ) {
    const storeCreditButtonConfig = {
      id: 'storeCredit',
      label: t('Store credit', {
        _context: 'Store Credit payment type button label',
      }),
      action: () => pay(storeCreditPaymentType),
      isDisabled: false,
    }
    paymentOptions.push(storeCreditButtonConfig)
  }

  // on account payment
  if (getFeatureFromCache(FEATURE_ON_ACCOUNT_PAYMENT_TYPE)) {
    // Payment-type flow: On Account is a proper payment (like store credit
    // / loyalty) that debits the customer's account balance, rather than a
    // sale-status transition. Rendered in the customer section alongside
    // Loyalty, Layaway, and Store credit.
    // Hidden without a valid customer; additional disable logic is owned by
    // isOnAccountPaymentTypeDisabled in usePaymentDisabledState.
    const onAccountPaymentType = systemPaymentTypes.onAccountPaymentType
    if (
      onAccountPaymentType &&
      hasValidCustomer &&
      (notReturnSaleAndPositiveBalance || hasXeroEnabled) &&
      currentSale.displayStatus !== SaleStatus.LAYBY_SALE &&
      userManager.hasPermission(PERMISSIONS.SALE_ON_ACCOUNT)
    ) {
      paymentOptions.push({
        id: 'onAccountPaymentType',
        label: t('On account', {
          _context: 'On Account payment type button label',
        }),
        action: () => pay(onAccountPaymentType),
        isDisabled: false,
      })
    }
  } else if (notReturnSaleAndPositiveBalance || hasXeroEnabled) {
    if (
      currentSale.displayStatus !== SaleStatus.LAYBY_SALE &&
      userManager.hasPermission(PERMISSIONS.SALE_ON_ACCOUNT)
    ) {
      // Legacy sale-status flow: transitions the sale to ONACCOUNT status.
      paymentOptions.push({
        id: SaleStatus.ACCOUNT_SALE,
        label: t('On account', {
          _context: 'On Account payment type button label',
        }),
        action: () => laybyOnAccountSale(SaleStatus.ACCOUNT_SALE),
        isDisabled: false,
      })
    }
  }

  // offline and unable to process this sale with loyalty and store credit
  const isOffline = !webRegisterStatusManager.isOnline()
  const isStoreCreditAndLoyaltyPaymentUnable =
    isOffline &&
    (isStoreCreditEnabled || loyaltyButtonConfig) &&
    hasValidCustomer
  return { paymentOptions, isStoreCreditAndLoyaltyPaymentUnable }
}
